import type { TvShow } from '@/types/tv';

export interface FilterOptions {
  minScore?: number;
  maxScore?: number;
  minYear?: number;
  maxYear?: number;
  tag?: string;
}

export const scoreMatches = (show: TvShow, options: FilterOptions) => {
  const { minScore, maxScore } = options;
  if (typeof minScore === 'number' && show.score < minScore) return false;
  if (typeof maxScore === 'number' && show.score > maxScore) return false;
  return true;
};

export const yearMatches = (show: TvShow, options: FilterOptions) => {
  const { minYear, maxYear } = options;
  if (typeof minYear === 'number' && show.year < minYear) return false;
  if (typeof maxYear === 'number' && show.year > maxYear) return false;
  return true;
};

export const tagMatches = (show: TvShow, options: FilterOptions) => {
  const { tag } = options;
  if (!tag) return true;
  return show.tags.includes(tag) || show.region === tag;
};

export const filterShows = (list: TvShow[], options: FilterOptions) =>
  list.filter(
    (show) =>
      scoreMatches(show, options) &&
      yearMatches(show, options) &&
      tagMatches(show, options),
  );

export type SortKey = 'score' | 'updated' | 'year';

export const sortShows = (list: TvShow[], key: SortKey) => {
  const copy = [...list];
  if (key === 'score') {
    return copy.sort((a, b) => b.score - a.score);
  }
  if (key === 'year') {
    return copy.sort((a, b) => b.year - a.year);
  }
  return copy.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
};

export const searchShows = (list: TvShow[], keyword: string) => {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return list.filter(
    (show) =>
      show.title.toLowerCase().includes(query) ||
      show.originalTitle.toLowerCase().includes(query) ||
      show.intro.toLowerCase().includes(query) ||
      show.synopsis.join('').toLowerCase().includes(query) ||
      show.tags.some((tag) => tag.toLowerCase().includes(query)) ||
      show.region.toLowerCase().includes(query) ||
      show.cast.some((member) => member.name.toLowerCase().includes(query)),
  );
};

export const getRelatedShows = (list: TvShow[], current: TvShow, limit = 6) => {
  const scored = list
    .filter((show) => show.id !== current.id)
    .map((show) => {
      const shared = show.categories.filter((cat) =>
        current.categories.includes(cat),
      ).length;
      const closeness = 10 - Math.abs(show.score - current.score);
      return { show, weight: shared * 10 + closeness };
    })
    .sort((a, b) => b.weight - a.weight);
  return scored.slice(0, limit).map((item) => item.show);
};

export const paginate = <T,>(list: T[], page: number, pageSize: number) =>
  list.slice(0, page * pageSize);

export const uniqueYears = (list: TvShow[]) => {
  const years = list.map((show) => show.year);
  return { min: Math.min(...years), max: Math.max(...years) };
};